import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getPickSuggestions,
  approvePickJob,
  type CustomerSuggestions,
  type SuggestionGroup,
  type OrderSummary,
} from "@/lib/pick-jobs.functions";
import {
  listOrderOperationalSummaries,
  type OrderSummaryLite,
} from "@/lib/orders/summary.functions";
import { OrderRef } from "@/components/orders/order-ref";
import { listAssignableUsers } from "@/lib/operational-signals.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { useCapabilities } from "@/hooks/use-capabilities";
import { Link } from "@tanstack/react-router";
import { PageHeader } from "@/components/operator-shell";
import { ScopeBar } from "@/components/scope-bar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, CheckCircle2, RefreshCw, Layers, Boxes, Package, MoreHorizontal, ArrowRightLeft, Users, User } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/pick/")({ component: PickSuggestions });

function WaveNavButton() {
  const caps = useCapabilities();
  if (!caps.isEnabled("wave_planning")) return null;
  return (
    <Button size="sm" variant="outline" asChild>
      <Link to="/app/pick/waves"><Layers className="h-4 w-4 mr-1" />Waves</Link>
    </Button>
  );
}

type JobType = "bulk_batch" | "pick_to_bin" | "order_by_order";

function AssigneePicker({
  tenantId, value, onChange,
}: {
  tenantId: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const listUsersFn = useServerFn(listAssignableUsers);
  const { data: users = [] } = useQuery({
    queryKey: ["pick-assignees", tenantId],
    enabled: !!tenantId,
    queryFn: () => listUsersFn({ data: { tenantId } }),
    staleTime: 60_000,
  });
  const selected = value === null ? "__pool__" : value;
  return (
    <Select
      value={selected}
      onValueChange={(v) => onChange(v === "__pool__" ? null : v)}
    >
      <SelectTrigger className="h-8 w-[180px]" aria-label="Assign to">
        <div className="flex items-center gap-1.5 text-xs">
          {value === null
            ? <Users className="h-3.5 w-3.5 text-muted-foreground" />
            : <User className="h-3.5 w-3.5 text-muted-foreground" />}
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__pool__">
          <span className="flex items-center gap-2"><Users className="h-3.5 w-3.5" />Pool (anyone)</span>
        </SelectItem>
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            <span className="flex items-center gap-2"><User className="h-3.5 w-3.5" />{u.display_name ?? u.id.slice(0, 8)}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const JOB_TYPE_LABEL: Record<JobType, string> = {
  bulk_batch: "Bulk / Batch",
  pick_to_bin: "Pick to Cart",
  order_by_order: "Order by Order",
};

/**
 * Sprint 36 closeout — `OrderLink` (bespoke button + inline preview Dialog)
 * was replaced with the canonical `<OrderRef>` hover card. This eliminates
 * a duplicate Information-Hierarchy Hover pattern and aligns Pick Release
 * with every other list that references an order by number.
 */
function OrderCell({ orderId, orderNumber, summary }: {
  orderId: string;
  orderNumber: string;
  summary: OrderSummaryLite | undefined;
}) {
  return (
    <OrderRef
      summary={summary}
      orderId={orderId}
      fallbackOrderNumber={orderNumber}
      className="font-mono text-xs"
    />
  );
}

function MoveOrderMenu({
  currentType, facilityId, customerId, orderId, onMoved,
}: {
  currentType: JobType;
  facilityId: string;
  customerId: string;
  orderId: string;
  onMoved: () => void;
}) {
  const approveFn = useServerFn(approvePickJob);
  const [busy, setBusy] = useState(false);
  const targets = (Object.keys(JOB_TYPE_LABEL) as JobType[]).filter((t) => t !== currentType);

  const moveTo = async (type: JobType) => {
    setBusy(true);
    try {
      const totes = type === "pick_to_bin" ? [""] : null;
      await runServerFn(
        () => approveFn({ data: { type, customerId, facilityId, orderIds: [orderId], toteCodes: totes } }),
        `Moving to ${JOB_TYPE_LABEL[type]}`,
      );
      toast.success(`Moved to ${JOB_TYPE_LABEL[type]}`);
      onMoved();
    } catch {
      // toast handled
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="flex items-center gap-2">
          <ArrowRightLeft className="h-3.5 w-3.5" />Move to…
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {targets.map((t) => (
          <DropdownMenuItem key={t} onSelect={() => moveTo(t)}>
            {JOB_TYPE_LABEL[t]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PickSuggestions() {
  const qc = useQueryClient();
  const { tenantId, facilityId, customerId } = useScope();
  const getSuggestionsFn = useServerFn(getPickSuggestions);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["pick-suggestions", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getSuggestionsFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null } }),
  });

  // Sprint 36 closeout — hydrate OrderSummaryLite so <OrderRef> hover
  // cards can render inline with the pick suggestions list.
  const summariesFn = useServerFn(listOrderOperationalSummaries);
  const allOrderIds = useMemo(() => {
    const ids: string[] = [];
    for (const c of (data?.customers ?? [])) {
      for (const g of c.identical) for (const o of g.orders) ids.push(o.id);
      for (const g of c.in_common) for (const o of g.orders) ids.push(o.id);
      for (const o of c.ad_hoc) ids.push(o.id);
    }
    return Array.from(new Set(ids));
  }, [data]);
  const { data: summaries = [] } = useQuery({
    queryKey: ["pick-order-summaries", allOrderIds],
    enabled: allOrderIds.length > 0,
    queryFn: () => summariesFn({ data: { orderIds: allOrderIds } }) as Promise<OrderSummaryLite[]>,
  });
  const summaryById = useMemo(() => {
    const m = new Map<string, OrderSummaryLite>();
    for (const s of summaries) m.set(s.identity.id, s);
    return m;
  }, [summaries]);

  const customers = data?.customers ?? [];
  const topThreshold = data?.threshold_pct ?? null;
  const subtitle =
    customers.length === 0
      ? "Allocated orders grouped per customer. Identical batches require 5+ orders; In-common threshold is configurable per customer."
      : topThreshold != null
        ? `Allocated orders grouped per customer. Identical 5+; In-common threshold: ${topThreshold}%.`
        : `Allocated orders grouped per customer. Identical 5+; In-common threshold varies per customer.`;

  const onApproved = () => {
    qc.invalidateQueries({ queryKey: ["pick-suggestions"] });
    qc.invalidateQueries({ queryKey: ["open-pick-jobs"] });
  };

  return (
    <>
      <PageHeader
        title="Pick Release"
        description={subtitle}
        actions={
          <div className="flex items-center gap-2">
            <ScopeBar />
            <WaveNavButton />
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                qc.invalidateQueries({ queryKey: ["pick-suggestions"] });
                qc.invalidateQueries({ queryKey: ["open-pick-jobs"] });
                refetch();
              }}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading suggestions…</div>
        ) : customers.length === 0 ? (
          <div className="border rounded-lg p-10 bg-card text-center text-muted-foreground">
            No allocated orders waiting to be grouped.
          </div>
        ) : (
          <Accordion type="multiple" defaultValue={customers.map((c) => `${c.customer_id}::${c.size_tier}`)} className="space-y-2">
            {customers.map((c) => (
              <CustomerBlock
                key={`${c.customer_id}::${c.size_tier}`}
                customer={c}
                facilityId={facilityId!}
                tenantId={tenantId!}
                onApproved={onApproved}
                summaryById={summaryById}
              />
            ))}
          </Accordion>
        )}
      </div>
    </>
  );
}

function CustomerBlock({
  customer, facilityId, tenantId, onApproved, summaryById,
}: {
  customer: CustomerSuggestions;
  facilityId: string;
  tenantId: string;
  onApproved: () => void;
  summaryById: Map<string, OrderSummaryLite>;
}) {
  const counts = customer.identical.length + customer.in_common.length + customer.ad_hoc.length;
  const tierLabel = customer.size_tier === "bulk" ? "Bulk / pallet" : customer.size_tier === "retail" ? "Retail compliance" : "Standard";
  const tierVariant = customer.size_tier === "standard" ? "outline" : "secondary";
  return (
    <AccordionItem value={`${customer.customer_id}::${customer.size_tier}`} className="border rounded-lg bg-card px-4">
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center gap-3">
          <span className="font-semibold">{customer.customer_name}</span>
          <Badge variant={tierVariant as any}>{tierLabel}</Badge>
          <Badge variant="secondary">{counts} suggestion{counts === 1 ? "" : "s"}</Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent className="space-y-3 pb-4">
        {customer.identical.length > 0 && (
          <BucketCard
            title="Identical"
            icon={<Layers className="h-4 w-4" />}
            description="Same SKUs and same quantities → Bulk / batch jobs. 1 SKU = single bin; multi-SKU = tote per SKU."
            type="bulk_batch"
            facilityId={facilityId}
            tenantId={tenantId}
            customerId={customer.customer_id}
            groups={customer.identical}
            onApproved={onApproved}
            summaryById={summaryById}
          />
        )}
        {customer.in_common.length > 0 && (
          <BucketCard
            title="In common"
            icon={<Boxes className="h-4 w-4" />}
            description="Mixed multi-line orders → Pick to Cart. Pickers assign a tote per order at the start of picking."
            type="pick_to_bin"
            facilityId={facilityId}
            tenantId={tenantId}
            customerId={customer.customer_id}
            groups={customer.in_common}
            onApproved={onApproved}
            summaryById={summaryById}
          />
        )}
        {customer.ad_hoc.length > 0 && (
          <AdHocCard
            facilityId={facilityId}
            tenantId={tenantId}
            customerId={customer.customer_id}
            orders={customer.ad_hoc}
            onApproved={onApproved}
            summaryById={summaryById}
          />
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function BucketCard({
  title, icon, description, type, facilityId, tenantId, customerId, groups, onApproved, summaryById,
}: {
  title: string;
  icon: React.ReactNode;
  description: string;
  type: JobType;
  facilityId: string;
  tenantId: string;
  customerId: string;
  groups: SuggestionGroup[];
  onApproved: () => void;
  summaryById: Map<string, OrderSummaryLite>;
}) {
  return (
    <div className="border rounded-lg">
      <div className="px-3 py-2 border-b flex items-center gap-2">
        {icon}
        <span className="font-medium">{title}</span>
        <span className="text-xs text-muted-foreground">— {description}</span>
      </div>
      <div className="divide-y">
        {groups.map((g) => (
          <GroupRow key={g.id} group={g} type={type} facilityId={facilityId} tenantId={tenantId} customerId={customerId} onApproved={onApproved} summaryById={summaryById} />
        ))}
      </div>
    </div>
  );
}

function GroupRow({
  group, type, facilityId, tenantId, customerId, onApproved, summaryById,
}: {
  group: SuggestionGroup;
  type: JobType;
  facilityId: string;
  tenantId: string;
  customerId: string;
  onApproved: () => void;
  summaryById: Map<string, OrderSummaryLite>;
}) {
  const approveFn = useServerFn(approvePickJob);
  const [selected, setSelected] = useState<Record<string, boolean>>(
    () => Object.fromEntries(group.orders.map((o) => [o.id, true])),
  );
  const [assigneeId, setAssigneeId] = useState<string | null>(null); // null = pool
  const [submitting, setSubmitting] = useState(false);

  const selectedIds = useMemo(
    () => group.orders.filter((o) => selected[o.id]).map((o) => o.id),
    [group.orders, selected],
  );

  const allSelected = group.orders.length > 0 && selectedIds.length === group.orders.length;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const toggleAll = (v: boolean) =>
    setSelected(Object.fromEntries(group.orders.map((o) => [o.id, v])));

  const approve = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one order");
    setSubmitting(true);
    try {
      await runServerFn(
        () => approveFn({ data: {
          type, customerId, facilityId,
          orderIds: selectedIds,
          toteCodes: null,
          assigneeId,
        } }),
        "Approving job",
      );
      toast.success(`Approved ${selectedIds.length} order${selectedIds.length === 1 ? "" : "s"}`);
      onApproved();
    } catch {
      // toast handled
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{group.orders.length} orders</Badge>
        {group.shared_skus.slice(0, 4).map((s) => (
          <Badge key={s.id} variant="secondary" className="font-mono text-xs">
            {s.sku_code} × {s.qty}
          </Badge>
        ))}
        {group.shared_skus.length > 4 && (
          <span className="text-xs text-muted-foreground">+{group.shared_skus.length - 4} more SKUs</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <AssigneePicker tenantId={tenantId} value={assigneeId} onChange={setAssigneeId} />
          <Button size="sm" onClick={approve} disabled={submitting || selectedIds.length === 0}>
            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Approve ({selectedIds.length})
          </Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected ? true : someSelected ? "indeterminate" : false}
                onCheckedChange={(v) => toggleAll(!!v)}
                aria-label="Select all"
              />
            </TableHead>
            <TableHead>Order</TableHead>
            <TableHead>Lines</TableHead>
            <TableHead>Qty</TableHead>
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {group.orders.map((o) => (
            <TableRow key={o.id}>
              <TableCell>
                <Checkbox
                  checked={!!selected[o.id]}
                  onCheckedChange={(v) => setSelected((s) => ({ ...s, [o.id]: !!v }))}
                />
              </TableCell>
              <TableCell><OrderCell orderId={o.id} orderNumber={o.order_number} summary={summaryById.get(o.id)} /></TableCell>
              <TableCell>{o.line_count}</TableCell>
              <TableCell>{o.total_qty}</TableCell>
              <TableCell className="text-right">
                <MoveOrderMenu
                  currentType={type}
                  facilityId={facilityId}
                  customerId={customerId}
                  orderId={o.id}
                  onMoved={onApproved}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AdHocCard({
  facilityId, tenantId, customerId, orders, onApproved, summaryById,
}: {
  facilityId: string;
  tenantId: string;
  customerId: string;
  orders: OrderSummary[];
  onApproved: () => void;
  summaryById: Map<string, OrderSummaryLite>;
}) {
  const approveFn = useServerFn(approvePickJob);
  const [selected, setSelected] = useState<Record<string, boolean>>(
    () => Object.fromEntries(orders.map((o) => [o.id, true])),
  );
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const selectedIds = useMemo(
    () => orders.filter((o) => selected[o.id]).map((o) => o.id),
    [orders, selected],
  );

  const allSelected = orders.length > 0 && selectedIds.length === orders.length;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const toggleAll = (v: boolean) =>
    setSelected(Object.fromEntries(orders.map((o) => [o.id, v])));

  const approve = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one order");
    setSubmitting(true);
    try {
      // Ad-hoc → one Order-by-order job per selected order.
      for (const id of selectedIds) {
        await runServerFn(
          () => approveFn({ data: {
            type: "order_by_order",
            customerId,
            facilityId,
            orderIds: [id],
            toteCodes: null,
            assigneeId,
          } }),
          "Approving job",
        );
      }
      toast.success(`Approved ${selectedIds.length} order${selectedIds.length === 1 ? "" : "s"}`);
      onApproved();
    } catch {
      // toast handled
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border rounded-lg">
      <div className="px-3 py-2 border-b flex items-center gap-2">
        <Package className="h-4 w-4" />
        <span className="font-medium">Ad hoc</span>
        <span className="text-xs text-muted-foreground">— Processed one at a time as Order-by-order jobs.</span>
        <div className="ml-auto flex items-center gap-2">
          <AssigneePicker tenantId={tenantId} value={assigneeId} onChange={setAssigneeId} />
          <Button size="sm" onClick={approve} disabled={submitting || selectedIds.length === 0}>
            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Approve ({selectedIds.length})
          </Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected ? true : someSelected ? "indeterminate" : false}
                onCheckedChange={(v) => toggleAll(!!v)}
                aria-label="Select all"
              />
            </TableHead>
            <TableHead>Order</TableHead>
            <TableHead>Lines</TableHead>
            <TableHead>Qty</TableHead>
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((o) => (
            <TableRow key={o.id}>
              <TableCell>
                <Checkbox
                  checked={!!selected[o.id]}
                  onCheckedChange={(v) => setSelected((s) => ({ ...s, [o.id]: !!v }))}
                />
              </TableCell>
              <TableCell><OrderCell orderId={o.id} orderNumber={o.order_number} summary={summaryById.get(o.id)} /></TableCell>
              <TableCell>{o.line_count}</TableCell>
              <TableCell>{o.total_qty}</TableCell>
              <TableCell className="text-right">
                <MoveOrderMenu
                  currentType="order_by_order"
                  facilityId={facilityId}
                  customerId={customerId}
                  orderId={o.id}
                  onMoved={onApproved}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}